'use client';

import * as React from 'react';
import { addDays, format } from 'date-fns';
import PropTypes from 'prop-types';

import { cn } from '../../../lib/utils';
import { Calendar } from '../../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';

export default function TaxFileDatePickerWithRange({
  className,
  onDateChange,
  initialDateRange = null,
}) {
  const [date, setDate] = React.useState(initialDateRange);
  const [isOpen, setIsOpen] = React.useState(false);

  // Sync internal state with prop changes
  React.useEffect(() => {
    setDate(initialDateRange);
  }, [initialDateRange]);

  // Handle date selection
  const handleDateSelect = selectedDate => {
    setDate(selectedDate);

    // Call the callback with the selected date range
    if (onDateChange) {
      if (selectedDate?.from && selectedDate?.to) {
        // Both dates selected
        onDateChange(selectedDate.from, selectedDate.to);
      } else if (selectedDate?.from) {
        // Only start date selected
        onDateChange(selectedDate.from, null);
      } else {
        // No dates selected
        onDateChange(null, null);
      }
    }
  };

  // Clear date range
  const handleClear = e => {
    e.stopPropagation();
    setDate(null);
    if (onDateChange) {
      onDateChange(null, null);
    }
  };

  // Check if there's an active filter
  const hasActiveFilter = date?.from || date?.to;

  return (
    <div className={cn('grid gap-2 z-20', className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <div className="relative flex items-center">
          {' '}
          {/* New wrapper div */}
          <PopoverTrigger asChild>
            <button
              id="date"
              className={cn(
                'btn px-2.5 pr-10 min-w-[15.5rem] bg-white border-gray-200 hover:border-gray-300 dark:border-gray-700/60 dark:hover:border-gray-600 dark:bg-gray-800 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 font-medium text-left justify-start', // Removed 'relative'
                !date && 'text-muted-foreground',
                hasActiveFilter &&
                  'border-primary-300 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20'
              )}
            >
              {/* Calendar Icon */}
              <svg
                className="fill-current text-gray-400 dark:text-gray-500 ml-1 mr-2 shrink-0"
                width="16"
                height="16"
                viewBox="0 0 16 16"
              >
                <path d="M5 4a1 1 0 0 0 0 2h6a1 1 0 1 0 0-2H5Z"></path>
                <path d="M4 0a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4V4a4 4 0 0 0-4-4H4ZM2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Z"></path>
              </svg>

              {/* Date Display - always show placeholder, do not reveal selected range */}
              <span className="text-sm text-gray-500 dark:text-gray-500 flex-1 truncate">
                <span>Filter by date</span>
              </span>

              {/* Active filter indicator */}
              {hasActiveFilter && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full"></div>
              )}
            </button>
          </PopoverTrigger>
          {/* Clear button - now a sibling, not a child */}
          {hasActiveFilter && (
            <button
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors" // Adjusted right position and removed ml-2
              title="Clear date filter"
            >
              <svg
                className="w-3 h-3 text-gray-400 dark:text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>{' '}
        {/* End of new wrapper div */}
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3">
            <Calendar
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={handleDateSelect}
              numberOfMonths={2}
            />

            {/* Quick date range options */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
              <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-2">
                Quick Select
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-600 dark:text-gray-300 uppercase"
                  onClick={() => {
                    const today = new Date();
                    const lastWeek = addDays(today, -7);
                    const newRange = { from: lastWeek, to: today };
                    setDate(newRange);
                    handleDateSelect(newRange);
                  }}
                >
                  Last 7 days
                </button>
                <button
                  className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-600 dark:text-gray-300 uppercase"
                  onClick={() => {
                    const today = new Date();
                    const lastMonth = addDays(today, -30);
                    const newRange = { from: lastMonth, to: today };
                    setDate(newRange);
                    handleDateSelect(newRange);
                  }}
                >
                  Last 30 days
                </button>
                <button
                  className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-600 dark:text-gray-300 uppercase"
                  onClick={() => {
                    const today = new Date();
                    const lastQuarter = addDays(today, -90);
                    const newRange = { from: lastQuarter, to: today };
                    setDate(newRange);
                    handleDateSelect(newRange);
                  }}
                >
                  Last 90 days
                </button>
                <button
                  className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 rounded text-red-600 dark:text-red-400 uppercase"
                  onClick={() => {
                    setDate(null);
                    handleDateSelect(null);
                    setIsOpen(false);
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

TaxFileDatePickerWithRange.propTypes = {
  className: PropTypes.string,
  onDateChange: PropTypes.func,
  initialDateRange: PropTypes.object,
};
